import { useCallback, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/reducerHooks';
import { getSavedGridState } from 'utils/local-storage';
import { SectionType } from 'pages/ClaimsContractsToolPage/types';
import { bookmarksApi } from 'api/endpoints/bookmarks';
import { error, success } from 'utils/alert';
import { isEmpty } from 'lodash';
import { getBookmarksThunk } from 'reducer/thunks/bookmarksThunk';
import { useAppUserType } from 'utils/hooks/useAppUserType';
import { BookmarkCardData } from '../types';

const BOOKMARK_SECTIONS = [
  SectionType.Claims,
  SectionType.Contracts,
  SectionType.Matching,
];

const FILTER_BY_SECTION = [
  String(SectionType.Claims),
  String(SectionType.Contracts),
  String(SectionType.Matching),
];

export const useBookmarksData = (activeBookmarkSection: SectionType) => {
  const [openModal, setOpenModal] = useState(false);
  const [bookmarkTitle, setBookmarkTitle] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const { userName } = useAppUserType();

  const dispatch = useAppDispatch();
  const { bookmarks = [] } = useAppSelector((s) => s.selectable.data);

  const filteredBookmarks = useMemo(() => {
    const filterKey: keyof BookmarkCardData = FILTER_BY_SECTION.includes(
      selectedOption,
    )
      ? 'section'
      : 'createdByEmail';
    const filterValue = filterKey === 'createdByEmail' ? userName : selectedOption;

    return filterKey && selectedOption
      ? bookmarks.filter((b) => b[filterKey] === filterValue)
      : [...bookmarks];
  }, [selectedOption, userName, bookmarks]);

  const {
    claimsBookmarksCount,
    contractsBookmarksCount,
    myBookmarksCount,
    matchingBookmarksCount,
    allBookmarksCount,
  } = useMemo(
    () => ({
      claimsBookmarksCount: bookmarks.filter((b) => b.section === SectionType.Claims)
        ?.length,
      contractsBookmarksCount: bookmarks.filter(
        (b) => b.section === SectionType.Contracts,
      )?.length,
      myBookmarksCount: bookmarks.filter((b) => b.createdByEmail === userName)
        ?.length,
      matchingBookmarksCount: bookmarks.filter(
        (b) => b.section === SectionType.Matching,
      )?.length,
      allBookmarksCount: bookmarks.length,
    }),
    [bookmarks, userName],
  );

  const handleBookmarkSave = useCallback(async () => {
    const currentSavedState = getSavedGridState();
    const sectionState = currentSavedState?.[activeBookmarkSection] ?? {};
    const response = await bookmarksApi.saveBookmark({
      section: activeBookmarkSection,
      filter: sectionState,
      title: bookmarkTitle,
    });

    if (isEmpty(response.error)) {
      success('Bookmark succesfully saved');
      dispatch(getBookmarksThunk());
    } else error("Bookmark couldn't be saved. Please try again.");
  }, [bookmarkTitle, activeBookmarkSection, dispatch]);

  const handleBookmarkEdit = useCallback(
    async (bookmarkId: number, newTitle: string) => {
      const response = await bookmarksApi.editBookmark({
        id: bookmarkId,
        title: newTitle,
      });
      if (isEmpty(response.error)) {
        success('Bookmark succesfully edited');
        dispatch(getBookmarksThunk());
      } else error("Bookmark couldn't be saved. Please try again.");
    },
    [dispatch],
  );

  const handleBookmarkDelete = useCallback(
    async (bookmarkId: number) => {
      const response = await bookmarksApi.deleteBookmark(bookmarkId);
      if (isEmpty(response.error)) {
        success('Bookmark succesfully deleted');
        dispatch(getBookmarksThunk());
      } else error("Bookmark couldn't be deleted. Please try again.");
    },
    [dispatch],
  );

  return {
    openModal,
    setOpenModal,
    handleBookmarkSave,
    handleBookmarkEdit,
    handleBookmarkDelete,
    setBookmarkTitle,
    bookmarkTitle,
    enableAddBookmark: useMemo(
      () => BOOKMARK_SECTIONS.includes(activeBookmarkSection),
      [activeBookmarkSection],
    ),
    setSelectedOption,
    filteredBookmarks,
    claimsBookmarksCount,
    contractsBookmarksCount,
    myBookmarksCount,
    matchingBookmarksCount,
    allBookmarksCount,
  };
};
